/**
 * Available Batches Page
 * View and filter batches available for order allocation
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAvailableBatches } from '@/hooks/useAvailableBatches';
import type { AvailableBatchResponse } from '@/types/availableBatches';
import { BatchSearchBar } from '@/components/inventory/batches/BatchSearchBar';
import { BatchSortSelector } from '@/components/inventory/batches/BatchSortSelector';
import { BatchFiltersPanel } from '@/components/inventory/batches/BatchFiltersPanel';
import { CompactBatchFilters } from '@/components/inventory/batches/CompactBatchFilters';
import { AvailableBatchCard } from '@/components/inventory/batches/AvailableBatchCard';
import { AvailableBatchesTable } from '@/components/inventory/batches/AvailableBatchesTable';
import { BatchDetailsDialog } from '@/components/inventory/batches/BatchDetailsDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package,
  Grid,
  List,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type ViewMode = 'grid' | 'table';

export default function AvailableBatchesPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState<number[]>([]);

  const {
    batches,
    filteredBatches,
    loading,
    error,
    filters,
    sort,
    searchQuery,
    setFilters,
    setSort,
    setSearchQuery,
    resetFilters,
    refetch,
    activeFilterCount
  } = useAvailableBatches();

  // Statistics
  const stats = {
    total: filteredBatches.length,
    normalStock: filteredBatches.filter(b => b.stockLevel === 'NORMAL').length,
    lowStock: filteredBatches.filter(b => b.stockLevel === 'LOW').length,
    criticalStock: filteredBatches.filter(b => b.stockLevel === 'CRITICAL').length,
    expiringSoon: filteredBatches.filter(b => b.daysRemaining <= 30).length
  };

  const handleViewDetails = (batch: AvailableBatchResponse) => {
    setSelectedBatchId(batch.id);
    setIsDetailsDialogOpen(true);
  };

  const handleAllocateToOrder = (batch: AvailableBatchResponse) => {
    // TODO: Navigate to order creation/allocation page with batch pre-selected
    toast.info('Allocate to Order', {
      description: `Allocating batch ${batch.batchNumber} to order (Feature in development)`
    });
    // navigate(`/sales/orders/create?batchId=${batch.id}`);
  };

  const handleRefresh = async () => {
    toast.promise(refetch(), {
      loading: 'Refreshing batches...',
      success: 'Batches refreshed successfully',
      error: 'Failed to refresh batches'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Available Batches
              </h1>
              <p className="text-gray-600 mt-2">
                View and allocate batches for order fulfillment
              </p>
            </div>
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="lg"
              disabled={loading}
            >
              <RefreshCw className={cn('mr-2 h-5 w-5', loading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Available</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
                </div>
                <Package className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Normal Stock</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{stats.normalStock}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Low Stock</p>
                  <p className="text-3xl font-bold text-orange-600 mt-2">{stats.lowStock}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Critical Stock</p>
                  <p className="text-3xl font-bold text-red-600 mt-2">{stats.criticalStock}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Expiring Soon</p>
                  <p className="text-3xl font-bold text-orange-600 mt-2">{stats.expiringSoon}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Warning/Error Alert */}
        {error && (
          <Alert
            className={cn(
              'mb-8',
              error.includes('sample data')
                ? 'border-amber-300 bg-amber-50'
                : 'border-red-300 bg-red-50'
            )}
          >
            {error.includes('sample data') ? (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertDescription
              className={cn(
                error.includes('sample data') ? 'text-amber-800' : 'text-red-800'
              )}
            >
              {error}
              <Button
                variant="link"
                size="sm"
                onClick={handleRefresh}
                className={cn(
                  'ml-2 underline',
                  error.includes('sample data') ? 'text-amber-700' : 'text-red-700'
                )}
              >
                <RefreshCw className="mr-1 h-3 w-3" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content - Full Width */}
        <div className="space-y-6">
          {/* Search, Filters and Controls - All in one row */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Search Bar + Inline Filters + Sort */}
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                  <div className="flex-1">
                    <BatchSearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                    />
                  </div>

                  {/* Compact Filters - Display beside search */}
                  <div className="w-full lg:w-auto">
                    <CompactBatchFilters
                      filters={filters}
                      onFiltersChange={setFilters}
                      onReset={resetFilters}
                      activeFilterCount={activeFilterCount}
                    />
                  </div>

                  {/* Sort Selector */}
                  <div className="flex-shrink-0">
                    <BatchSortSelector sort={sort} onSortChange={setSort} />
                  </div>
                </div>

                {/* View Mode Toggle and Results Info */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2 border-t">
                  <div className="text-sm text-gray-600">
                    Showing <span className="font-semibold text-gray-900">{filteredBatches.length}</span> of <span className="font-semibold text-gray-900">{batches.length}</span> batches
                    {activeFilterCount > 0 && (
                      <span className="ml-2 text-blue-600 font-medium">
                        ({activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid className="h-4 w-4 mr-2" />
                      Grid
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('table')}
                    >
                      <List className="h-4 w-4 mr-2" />
                      Table
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className={cn(
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'
                : 'space-y-4'
            )}>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card key={i}>
                  <CardContent className="pt-6 space-y-4">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredBatches.length === 0 && (
            <Card className="py-16">
              <CardContent className="flex flex-col items-center justify-center text-center">
                <Package className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No batches found
                </h3>
                <p className="text-gray-600 mb-6 max-w-md">
                  {activeFilterCount > 0 || searchQuery
                    ? 'Try adjusting your filters or search query to see more results'
                    : 'No available batches at the moment'}
                </p>
                {(activeFilterCount > 0 || searchQuery) && (
                  <Button onClick={resetFilters}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Grid View */}
          {!loading && filteredBatches.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredBatches.map(batch => (
                <AvailableBatchCard
                  key={batch.id}
                  batch={batch}
                  onViewDetails={() => handleViewDetails(batch)}
                  onAllocateToOrder={() => handleAllocateToOrder(batch)}
                />
              ))}
            </div>
          )}

          {/* Table View */}
          {!loading && filteredBatches.length > 0 && viewMode === 'table' && (
            <AvailableBatchesTable
              batches={filteredBatches}
              sort={sort}
              onSortChange={setSort}
              onViewDetails={handleViewDetails}
              onAllocateToOrder={handleAllocateToOrder}
              selectedBatches={selectedBatches}
              onSelectionChange={setSelectedBatches}
            />
          )}
        </div>

        {/* Batch Details Dialog */}
        <BatchDetailsDialog
          batchId={selectedBatchId}
          open={isDetailsDialogOpen}
          onOpenChange={setIsDetailsDialogOpen}
        />
      </div>
    </div>
  );
}
